// src/lib/schema/base/organization.js

import { shopInfoContainer } from '@/containers/shopInfo.container';
import { validateRequiredFields, cleanObject } from '@/lib/schema';


/**
 * Creates structured data for the organization
 * @param {object} options - Optional configuration
 * @returns {object} - JSON-LD schema for the organization
 */
export function generateOrganizationSchema(options = {}) {
  const {
    baseURL,
    googleMapsPlaceID,
    name: shopName,
    address,
    phone,
    email = 'info@yourcarpet.com',
    logo,
    socialMedia = {}
  } = { ...shopInfoContainer, ...options };

  // Basic validation
  if (!validateRequiredFields({ baseURL, shopName }, ['baseURL', 'shopName'])) {
    console.warn('Missing required organization fields for schema generation');
    return null;
  }

  // Build social media links
  const socialLinks = [];

  if (socialMedia.facebookPageURL) {
    socialLinks.push(socialMedia.facebookPageURL);
  } else if (options.defaultFacebookURL) {
    socialLinks.push(options.defaultFacebookURL);
  } else {
    socialLinks.push('https://www.facebook.com/yourcarpet');
  }

  if (socialMedia.instagramPageURL) {
    socialLinks.push(socialMedia.instagramPageURL);
  } else if (options.defaultInstagramURL) {
    socialLinks.push(options.defaultInstagramURL);
  } else {
    socialLinks.push('https://www.instagram.com/yourcarpet');
  }

  if (socialMedia.twitterPageURL) {
    socialLinks.push(socialMedia.twitterPageURL);
  }

  if (socialMedia.pinterestPageURL) {
    socialLinks.push(socialMedia.pinterestPageURL);
  } else {
    socialLinks.push('https://www.pinterest.com/yourcarpet');
  }

  // Create organization schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseURL}/#organization`,
    'name': shopName,
    'url': baseURL,
    'logo': logo ? logo : `${baseURL}/logo.png`,
    'email': email
  };

  // Add phone if available
  if (phone) {
    schema.telephone = phone;
  }

  // Add social media links if available
  if (socialLinks.length > 0) {
    schema.sameAs = socialLinks.filter(Boolean);
  }

  // Add address if available
  if (address) {
    schema.address = {
      '@type': 'PostalAddress',
      'streetAddress': address.street || '',
      'addressLocality': address.city || '',
      'postalCode': address.postalCode || '',
      'addressCountry': address.country || ''
    };
  }

  // Add Google Maps link if available
  if (googleMapsPlaceID) {
    schema.hasMap = `https://www.google.com/maps?cid=${googleMapsPlaceID}`;
  }

  // Add opening hours if available
  if (options.openingHours && Array.isArray(options.openingHours)) {
    schema.openingHoursSpecification = options.openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': hours.days,
      'opens': hours.opens,
      'closes': hours.closes
    }));
  }

  return cleanObject(schema);
}
